import { logError } from '../lib/logger';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { InlineSelect } from './StatusBadge';
import { getSaleContingencies, createSaleContingency, updateSaleContingency, deleteSaleContingency, getTeamMembers, extendContingency } from '../lib/api';
import { Plus, PencilSimple, Trash, Warning, CalendarBlank, Phone, ShieldCheck, XCircle, Eye, CaretDown, CaretUp } from '@phosphor-icons/react';
import { toast } from 'sonner';

const C_STATUSES = ['Active', 'Extended', 'Expiring Soon', 'Met', 'Expired'];
const HANDLING = ['Pending Review', 'Will Extend', 'Will Meet Deadline', 'Needs Discussion', 'Requesting Extension', 'Resolved'];
const CONTACT_METHODS = ['Not Started', 'Called - Awaiting Response', 'Called + Email Sent', 'Confirmed', 'Failed - Inquired on Date'];

const HANDLING_COLORS = {
  'Pending Review': 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  'Will Extend': 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  'Will Meet Deadline': 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  'Needs Discussion': 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  'Requesting Extension': 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30',
  'Resolved': 'bg-stone-500/15 text-stone-600 dark:text-stone-400 border-stone-500/30',
};

const CONTACT_COLORS = {
  'Not Started': 'bg-stone-500/15 text-stone-600 dark:text-stone-400 border-stone-500/30',
  'Called - Awaiting Response': 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  'Called + Email Sent': 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  'Confirmed': 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  'Failed - Inquired on Date': 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
};

const STATUS_COLORS = {
  'Active': 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  'Extended': 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  'Expiring Soon': 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  'Met': 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30',
  'Expired': 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
};

const RULES = [
  { icon: ShieldCheck, text: 'ALWAYS extend 48 hours BEFORE expiration. We do NOT wait until day of.', severity: 'critical' },
  { icon: XCircle, text: 'If they inquire on the date, WE HAVE FAILED. Stay ahead of deadlines.', severity: 'critical' },
  { icon: Phone, text: 'We CALL FIRST, then send email. We NEVER only do email.', severity: 'warning' },
  { icon: Eye, text: 'Monitor the timeline below. Yellow = action needed. Red = overdue.', severity: 'info' },
];

const SEVERITY_STYLES = {
  critical: 'bg-red-500/10 border-red-500/30 text-red-500 dark:text-red-400',
  warning: 'bg-amber-500/10 border-amber-500/30 text-amber-500 dark:text-amber-400',
  info: 'bg-muted border-border text-foreground',
};

const DAYS_STYLES = {
  overdue: 'text-red-500 dark:text-red-400',
  urgent: 'text-amber-500 dark:text-amber-400',
  normal: 'text-foreground',
  safe: 'text-muted-foreground',
};

function getDaysSeverity(days) {
  if (days <= 0) return 'overdue';
  if (days <= 2) return 'urgent';
  if (days <= 14) return 'normal';
  return 'safe';
}

function ExpandableNotes({ value, id, editingNotes, setEditingNotes, onBlur, testIdPrefix }) {
  const [expanded, setExpanded] = useState(false);
  const text = editingNotes[id] !== undefined ? editingNotes[id] : (value || '');
  const isLong = text.length > 25;

  return (
    <div>
      {expanded ? (
        <textarea value={text}
          onChange={(e) => setEditingNotes(p => ({ ...p, [id]: e.target.value }))}
          onBlur={(e) => onBlur(id, e.target.value)}
          className="w-full text-xs bg-muted/50 border border-border rounded-md p-2 text-foreground focus:border-primary focus:outline-none resize-none min-h-[70px]"
          data-testid={`${testIdPrefix}-expanded-${id}`} />
      ) : (
        <input type="text" value={text}
          onChange={(e) => setEditingNotes(p => ({ ...p, [id]: e.target.value }))}
          onBlur={(e) => onBlur(id, e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          className="w-full text-xs bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 text-foreground truncate"
          data-testid={`${testIdPrefix}-${id}`} />
      )}
      {isLong && (
        <button onClick={() => setExpanded(p => !p)}
          className="flex items-center gap-0.5 text-[10px] text-primary hover:text-primary/80 font-medium mt-0.5 transition-colors"
          data-testid={`${testIdPrefix}-toggle-${id}`}>
          {expanded ? <><CaretUp size={10} /> Less</> : <><CaretDown size={10} /> More</>}
        </button>
      )}
    </div>
  );
}

export default function SaleContingencies() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ borrower: '', status: 'Active', expiration_date: '', handling: 'Pending Review', contact_method: 'Not Started', notes: '', assigned_to: '' });
  const [editingNotes, setEditingNotes] = useState({});
  const [viewMode, setViewMode] = useState('table');

  const loadData = useCallback(async () => {
    try { const [d, m] = await Promise.all([getSaleContingencies(), getTeamMembers()]); setItems(d); setMembers(m); }
    catch (e) { logError('Commitment dates load error:', e); toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); const iv = setInterval(loadData, 5000); return () => clearInterval(iv); }, [loadData]);

  const assignOptions = useMemo(() => ['Unassigned', ...members.map(m => m.initials)], [members]);

  const getDaysLeft = (d) => { if (!d) return null; return Math.ceil((new Date(d) - new Date()) / 86400000); };
  const handleInlineUpdate = async (id, field, value) => {
    try { await updateSaleContingency(id, { [field]: value }); setItems(p => p.map(i => i.id === id ? { ...i, [field]: value } : i)); }
    catch (e) { toast.error('Failed'); }
  };
  const handleNotesBlur = async (id, val) => { if (editingNotes[id] !== undefined) { await handleInlineUpdate(id, 'notes', val); setEditingNotes(p => { const n = {...p}; delete n[id]; return n; }); } };
  const handleSave = async () => {
    if (!form.borrower.trim()) { toast.error('Borrower required'); return; }
    try {
      if (editing) { await updateSaleContingency(editing.id, form); toast.success('Updated'); }
      else { await createSaleContingency(form); toast.success('Created'); }
      setDialogOpen(false); setEditing(null); setForm({ borrower: '', status: 'Active', expiration_date: '', handling: 'Pending Review', contact_method: 'Not Started', notes: '', assigned_to: '' }); loadData();
    } catch (e) { toast.error('Failed'); }
  };
  const handleDelete = async (id) => { try { await deleteSaleContingency(id); toast.success('Deleted'); loadData(); } catch (e) { toast.error('Failed'); } };
  const handleExtend = async (id, days) => {
    try { const r = await extendContingency(id, days); toast.success(`Extended to ${r.new_date}`); loadData(); } catch (e) { toast.error('Failed to extend'); }
  };
  const handleEdit = (item) => {
    setEditing(item); setForm({ borrower: item.borrower, status: item.status, expiration_date: item.expiration_date || '', handling: item.handling || 'Pending Review', contact_method: item.contact_method || 'Not Started', notes: item.notes || '', assigned_to: item.assigned_to || '' });
    setDialogOpen(true);
  };

  const sorted = [...items].sort((a, b) => {
    const da = getDaysLeft(a.expiration_date), db = getDaysLeft(b.expiration_date);
    if (da === null) return 1; if (db === null) return -1; return da - db;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-4" data-testid="sale-contingencies-view">
      {/* NON-NEGOTIABLE STANDARDS */}
      <Card className="border-2 border-red-500/30 bg-card" data-testid="contingency-rules">
        <CardHeader className="pb-3 pt-4 px-5">
          <CardTitle className="text-lg text-red-500 dark:text-red-400 flex items-center gap-2.5 uppercase tracking-wide">
            <ShieldCheck size={22} weight="fill" className="text-red-500" />
            Non-Negotiable Standards
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {RULES.map((rule) => (
              <div key={rule.text.slice(0, 20)} className={`flex items-start gap-3 p-4 rounded-lg border-2 text-sm leading-relaxed font-medium ${SEVERITY_STYLES[rule.severity] || SEVERITY_STYLES.info}`} data-testid={`rule-${rule.severity}`}>
                <rule.icon size={22} weight="fill" className="flex-shrink-0 mt-0.5" />
                <span>{rule.text}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-muted-foreground">{items.length} commitment date{items.length !== 1 ? 's' : ''}</p>
          <div className="flex gap-0.5 bg-muted rounded-md p-0.5 ml-2">
            <button onClick={() => setViewMode('table')} className={`px-2 py-1 text-xs font-medium rounded ${viewMode === 'table' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="view-table">Table</button>
            <button onClick={() => setViewMode('timeline')} className={`px-2 py-1 text-xs font-medium rounded ${viewMode === 'timeline' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="view-timeline">Timeline</button>
          </div>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ borrower: '', status: 'Active', expiration_date: '', handling: 'Pending Review', contact_method: 'Not Started', notes: '', assigned_to: '' }); setDialogOpen(true); }}
          className="bg-primary text-primary-foreground hover:opacity-90 h-9 whitespace-nowrap" data-testid="add-contingency-btn">
          <Plus size={16} className="mr-1.5" /> <span className="hidden sm:inline">Add Commitment Date</span><span className="sm:hidden">Add</span>
        </Button>
      </div>

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <div className="space-y-3" data-testid="timeline-view">
          {sorted.map((item) => {
            const days = getDaysLeft(item.expiration_date);
            const isOverdue = days !== null && days <= 0;
            const isUrgent = days !== null && days <= 2;
            const isWarning = days !== null && days <= 5;
            const barWidth = days !== null ? Math.max(5, Math.min(100, (days / 30) * 100)) : 0;
            const borderCls = isOverdue ? 'border-red-500/40' : (isUrgent ? 'border-amber-500/40' : 'border-border');
            const textCls = isOverdue ? 'text-red-500' : (isUrgent ? 'text-amber-500' : 'text-foreground');
            const barCls = isOverdue ? 'bg-red-500' : (isUrgent ? 'bg-amber-500' : (isWarning ? 'bg-amber-400' : 'bg-emerald-500'));
            return (
              <Card key={item.id} className={`border bg-card ${borderCls}`} data-testid={`timeline-item-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-foreground text-base">{item.borrower}</span>
                      <InlineSelect value={item.status} options={C_STATUSES} onChange={(v) => handleInlineUpdate(item.id, 'status', v)} testId={`tl-status-${item.id}`} colorMap={STATUS_COLORS} />
                      <InlineSelect value={item.handling || 'Pending Review'} options={HANDLING} onChange={(v) => handleInlineUpdate(item.id, 'handling', v)} testId={`tl-handling-${item.id}`} colorMap={HANDLING_COLORS} />
                      <InlineSelect value={item.contact_method || 'Not Started'} options={CONTACT_METHODS} onChange={(v) => handleInlineUpdate(item.id, 'contact_method', v)} testId={`tl-contact-${item.id}`} colorMap={CONTACT_COLORS} />
                    </div>
                    <div className="flex items-center gap-2">
                      {days !== null && (
                        <span className={`text-sm font-bold ${textCls}`}>
                          {isOverdue && <Warning size={14} weight="fill" className="inline mr-1 animate-pulse-urgent" />}
                          {days} day{days !== 1 ? 's' : ''}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">{item.expiration_date || 'No date'}</span>
                    </div>
                  </div>
                  <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden mb-2">
                    <div className={`h-full rounded-full transition-all ${barCls}`} style={{ width: `${barWidth}%` }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground truncate max-w-[60%]">{item.notes || 'No notes'}</p>
                    <div className="flex items-center gap-2">
                      {item.assigned_to && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{item.assigned_to}</span>}
                      {isUrgent && !isOverdue && <span className="text-[10px] font-semibold text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded">EXTEND NOW</span>}
                      {isOverdue && <span className="text-[10px] font-semibold text-red-500 bg-red-500/10 px-1.5 py-0.5 rounded animate-pulse-urgent">OVERDUE</span>}
                      <div className="flex gap-1">
                        <button onClick={() => handleExtend(item.id, 7)} className="text-[10px] px-2 py-0.5 rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-500/25 transition-colors" data-testid={`extend-7-${item.id}`}>+7d</button>
                        <button onClick={() => handleExtend(item.id, 14)} className="text-[10px] px-2 py-0.5 rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-500/25 transition-colors" data-testid={`extend-14-${item.id}`}>+14d</button>
                        <button onClick={() => handleExtend(item.id, 30)} className="text-[10px] px-2 py-0.5 rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-500/25 transition-colors" data-testid={`extend-30-${item.id}`}>+30d</button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {sorted.length === 0 && <Card className="border border-border bg-card"><CardContent className="py-8 text-center text-muted-foreground text-sm">No commitment dates</CardContent></Card>}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card className="border border-border bg-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pl-4">Borrower</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Status</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Commitment Date</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Days Left</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Handling</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Contact</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Assigned</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Notes</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pr-4 w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No commitment dates</TableCell></TableRow> :
                sorted.map(item => {
                  const days = getDaysLeft(item.expiration_date);
                  return (
                    <TableRow key={item.id} className="border-border hover:bg-muted/30" data-testid={`contingency-row-${item.id}`}>
                      <TableCell className="font-medium text-foreground pl-4 text-sm">{item.borrower}</TableCell>
                      <TableCell><InlineSelect value={item.status} options={C_STATUSES} onChange={(v) => handleInlineUpdate(item.id, 'status', v)} testId={`inline-c-status-${item.id}`} colorMap={STATUS_COLORS} /></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarBlank size={13} />
                          <input type="date" value={item.expiration_date || ''} onChange={(e) => handleInlineUpdate(item.id, 'expiration_date', e.target.value)}
                            className="bg-transparent border-0 text-xs text-foreground focus:outline-none cursor-pointer" data-testid={`inline-c-date-${item.id}`} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {days !== null ? (
                          <span className={`flex items-center gap-1 text-xs font-semibold ${DAYS_STYLES[getDaysSeverity(days)]}`}>
                            {days <= 2 && <Warning size={13} weight="fill" className={days <= 0 ? 'animate-pulse-urgent' : ''} />}
                            {days} day{days !== 1 ? 's' : ''}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell><InlineSelect value={item.handling || 'Pending Review'} options={HANDLING} onChange={(v) => handleInlineUpdate(item.id, 'handling', v)} testId={`inline-c-handling-${item.id}`} colorMap={HANDLING_COLORS} /></TableCell>
                      <TableCell><InlineSelect value={item.contact_method || 'Not Started'} options={CONTACT_METHODS} onChange={(v) => handleInlineUpdate(item.id, 'contact_method', v)} testId={`inline-c-contact-${item.id}`} colorMap={CONTACT_COLORS} /></TableCell>
                      <TableCell><InlineSelect value={item.assigned_to || 'Unassigned'} options={assignOptions} onChange={(v) => handleInlineUpdate(item.id, 'assigned_to', v === 'Unassigned' ? '' : v)} testId={`inline-c-assigned-${item.id}`} /></TableCell>
                      <TableCell className="max-w-[200px]">
                        <ExpandableNotes value={item.notes} id={item.id} editingNotes={editingNotes}
                          setEditingNotes={setEditingNotes} onBlur={handleNotesBlur} testIdPrefix="inline-c-notes" />
                      </TableCell>
                      <TableCell className="pr-4">
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid={`edit-contingency-${item.id}`}><PencilSimple size={14} /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="h-7 w-7 text-red-400 hover:text-red-600" data-testid={`delete-contingency-${item.id}`}><Trash size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="contingency-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-lg">{editing ? 'Edit' : 'Add'} Commitment Date</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Borrower</label>
              <Input value={form.borrower} onChange={(e) => setForm(p => ({ ...p, borrower: e.target.value }))} className="border-border h-10" data-testid="input-contingency-borrower" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}><SelectTrigger className="border-border h-10" data-testid="select-contingency-status"><SelectValue /></SelectTrigger><SelectContent>{C_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Commitment Date</label>
                <Input type="date" value={form.expiration_date} onChange={(e) => setForm(p => ({ ...p, expiration_date: e.target.value }))} className="border-border h-10" data-testid="input-contingency-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Handling</label>
                <Select value={form.handling} onValueChange={(v) => setForm(p => ({ ...p, handling: v }))}><SelectTrigger className="border-border h-10"><SelectValue /></SelectTrigger><SelectContent>{HANDLING.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Contact Method</label>
                <Select value={form.contact_method} onValueChange={(v) => setForm(p => ({ ...p, contact_method: v }))}><SelectTrigger className="border-border h-10"><SelectValue /></SelectTrigger><SelectContent>{CONTACT_METHODS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Assigned To</label>
              <Select value={form.assigned_to || 'none'} onValueChange={(v) => setForm(p => ({ ...p, assigned_to: v === 'none' ? '' : v }))}><SelectTrigger className="border-border h-10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Unassigned</SelectItem>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} - {m.name}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Notes</label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="border-border min-h-[60px]" data-testid="input-contingency-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} className="bg-primary text-primary-foreground">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
